import React, { createContext, useContext, useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

// Helper functions for read poems tracking
const getReadPoemsKey = (userEmail) => `readPoems_${userEmail}`;

const getReadPoems = (userEmail) => {
  if (!userEmail) return [];
  const stored = localStorage.getItem(getReadPoemsKey(userEmail));
  return stored ? JSON.parse(stored) : [];
};

const markPoemAsRead = (userEmail, poemId) => {
  if (!userEmail || !poemId) return;
  const readPoems = getReadPoems(userEmail);
  if (!readPoems.includes(poemId)) {
    readPoems.push(poemId);
    localStorage.setItem(getReadPoemsKey(userEmail), JSON.stringify(readPoems));
  }
};

export function NotificationProvider({ children }) {
  const { currentUser } = useAuth();
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [readPoems, setReadPoems] = useState([]);

  // Load read poems from localStorage when user changes
  useEffect(() => {
    if (currentUser?.email) {
      const userReadPoems = getReadPoems(currentUser.email);
      setReadPoems(userReadPoems);
    }
  }, [currentUser]);

  // Listen for new poems
  useEffect(() => {
    if (!currentUser) return;

    const poemsRef = ref(database, 'poetry/entries');

    const unsubscribe = onValue(poemsRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const poems = [];
      snapshot.forEach((childSnapshot) => {
        poems.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });

      // Check if there are any unread poems from partner
      const unreadPoems = poems.filter(poem =>
        poem.authorEmail !== currentUser.email &&
        !readPoems.includes(poem.id)
      );

      // If there are unread poems, show the bird
      if (unreadPoems.length > 0) {
        // Sort by createdAt descending (newest first)
        unreadPoems.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });

        const latestUnread = unreadPoems[0];

        // Create notification for the latest unread poem
        const newNotification = {
          poemId: latestUnread.id,
          author: latestUnread.author,
          authorEmail: latestUnread.authorEmail,
          title: latestUnread.title,
          medium: latestUnread.medium,
          createdAt: latestUnread.createdAt,
          unreadCount: unreadPoems.length
        };

        setNotification(newNotification);
        setIsVisible(true);
      } else {
        // No unread poems, hide the bird
        setIsVisible(false);
        setNotification(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, readPoems]);

  // Dismiss notification - hides bird temporarily
  const dismissNotification = () => {
    setIsVisible(false);
  };

  // Function to mark a poem as read
  const markAsRead = (poemId) => {
    if (currentUser?.email && poemId) {
      markPoemAsRead(currentUser.email, poemId);
      const updatedReadPoems = getReadPoems(currentUser.email);
      setReadPoems(updatedReadPoems);
    }
  };

  // Function to check if a poem is read
  const isPoemRead = (poemId) => {
    return readPoems.includes(poemId);
  };

  const value = {
    notification,
    isVisible,
    dismissNotification,
    markAsRead,
    isPoemRead,
    readPoems
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
